// One call from plot and rules to ranked, checked retail park masterplans.
import { area, clean, difference, distanceTo, intersection, union, type MultiPoly, type Pt } from "../geom.ts";
import { envelope, type Envelope } from "../envelope.ts";
import { indicators, limits, type Indicators } from "../indicators.ts";
import { overshadowing } from "../context.ts";
import { DEFAULT_LATITUDE } from "../sun.ts";
import type { Check } from "../programme.ts";
import { DEFAULT_PROGRAMME, type Rules, type Site } from "../rules.ts";
import { CUT_ABOVE_4_SOURCE, CUT_SOURCE, DEROGATION, EXISTING_SOURCE, POT_SOURCE } from "../../rules/catuc.ts";
import {
  ACCESSIBLE_SHARE, ACCESSIBLE_SOURCE, DOCK_APRON, DOCK_APRON_SOURCE, EV_NONRES_SOURCE, EV_SHARE_NONRES, EXIT_RETAIL_SOURCE,
  FIRE_ACCESS_SOURCE, FIRE_ROAD_WIDTH, MAX_EXIT_DISTANCE_RETAIL, MAX_WALK_TO_ENTRANCE, MIN_SETBACK_SOURCE, RETAIL_PARKING_SOURCE,
  RETAIL_PARKING_STEPS, SUNLIGHT_SOURCE, WALK_SOURCE, type Source,
} from "../../rules/ro.ts";
import {
  big, finishParking, frontOf, layUnits, packRegion, parkingRegion, prepare, readPlot, trimmed, truckGates, typologies,
  type Prepared, type RetailTypology, type Rows, type Scheme,
} from "./masterplan.ts";
import { GLA_RATIO, type RetailKind, type RetailProgramme } from "./programme.ts";

export interface RetailParking {
  required: number;
  /** What the norm asks (the PUZ rate, or the RGU steps). */
  byLaw: number;
  /** What the tenants ask: the programme's spaces per 100 m² GLA. */
  target: number;
  fromRules: boolean;
  capacity: number;
  /** Of which on the pads' own lots. */
  onPads: number;
  accessible: number;
  ev: number;
  islands: number;
  converted: number;
  walkMax: number;
  walkMedian: number;
  /** m² of plot per space, all paving included: the figure to compare with 25–30. */
  perSpace: number;
}

export interface RetailBalance { plot: number; built: number; canopy: number; roads: number; parking: number; service: number; green: number }

export interface RetailGenerated {
  id: string;
  typology: RetailTypology;
  scheme: Scheme;
  footprintArea: number;
  grossArea: number;
  gla: number;
  height: number;
  indicators: Indicators;
  parking: RetailParking;
  balance: RetailBalance;
  byKind: { kind: RetailKind | "pad"; count: number; gla: number }[];
  /** What stopped the row growing, in a developer's words. */
  binding: string[];
  checks: Check[];
}

export interface RetailResult {
  envelope: Envelope;
  options: RetailGenerated[];
  /** Why there are none, when there are none. */
  message?: string;
}

const src = (s: Source) => [s.act, s.article].filter(Boolean).join(", ");
const fmt = (n: number, dp = 2) => n.toLocaleString("ro-RO", { maximumFractionDigits: dp });

/** Spaces the norm requires: the PUZ's rate per 100 m² GLA if it gives one, else the RGU steps on built area. */
export function requiredByLaw(rules: Rules, gross: number, gla: number): number {
  if (rules.parkingPerGla !== undefined) return Math.ceil((rules.parkingPerGla * gla) / 100);
  const step = RETAIL_PARKING_STEPS.find((s) => gross <= s.upTo)!;
  return Math.ceil(gross / step.m2PerSpace);
}

interface Trial {
  wings: MultiPoly[]; building: MultiPoly; canopy: MultiPoly; frontRoad: MultiPoly; service: MultiPoly; region: MultiPoly;
  gross: number; gla: number; projection: number; required: number;
}

function trial(p: Prepared, rules: Rules, k: number): Trial {
  const wings = trimmed(p, k);
  const { building, canopy, frontRoad, service } = frontOf(p, wings);
  const region = parkingRegion(p, building, canopy, frontRoad, service);
  const pads = p.pads.reduce((t, x) => t + x.area, 0);
  const gross = area(building) + pads;
  const gla = area(building) * GLA_RATIO + pads * GLA_RATIO;
  const catuc = (rules.regime ?? "catuc") === "catuc";
  const projection = (catuc ? area(union(building, canopy, ...p.pads.map((x) => x.building))) : gross) + (rules.existing?.footprint ?? 0);
  const required = Math.max(requiredByLaw(rules, gross, gla), Math.ceil((p.prog.parkingTarget * gla) / 100));
  return { wings, building, canopy, frontRoad, service, region, gross, gla, projection, required };
}

/** Paved area of the pads' lots: building, drive-through lane, their spaces and aisles. */
const padsPaved = (p: Prepared) => p.pads.reduce((t, x) => t + area(x.building) + area(x.lane) + area(x.aisles) + x.stalls.length * STALL_M2, 0);
const STALL_M2 = 12.5;
const capacityOf = (s: Scheme) => s.parking.stalls.length + s.pads.reduce((t, x) => t + x.stalls.length, 0);

/**
 * Green left by a trial with `stalls` spaces and `aisles` m² of aisle: what
 * is not building, canopy, road, yard or car park. Stalls and aisles lie in
 * the parking region, which is disjoint from everything else, so areas add.
 */
function greenArea(p: Prepared, t: Trial, stalls: number, aisles: number): number {
  return area(p.b.env.plot) - area(union(t.building, t.canopy, t.service, p.lanes, p.access, t.frontRoad)) - stalls * STALL_M2 - aisles - padsPaved(p);
}

function scheme(p: Prepared, rules: Rules, k: number, t: Trial, rows?: Rows): Scheme {
  const { units, columns } = layUnits(p, t.wings, t.building);
  const packed = packRegion(p, t.region, { rows });
  const padStalls = p.pads.reduce((s, x) => s + x.stalls.length, 0);
  const entrances: Pt[] = [...units.map((u) => u.entrance), ...p.pads.map((x) => x.entrance)];
  const plot = p.b.env.plot;
  const greenShort = rules.minGreen !== undefined ? (rules.minGreen / 100) * area(plot) - greenArea(p, t, packed.stalls.length, area(packed.aisles)) : 0;
  const parking = finishParking(packed, entrances, p.prog.treeEvery, t.required, padStalls, greenShort);
  const green = big(clean(difference(plot, t.building, t.canopy, t.service, p.lanes, p.access, t.frontRoad, parking.aisles, ...parking.stalls,
    ...p.pads.flatMap((x) => [x.building, x.lane, x.aisles, ...x.stalls]))), 0.5);
  const sf = p.b.sf;
  return {
    typology: p.typology, depth: p.depth, trim: k, spines: p.spines, wings: t.wings, building: t.building, units, pads: p.pads, columns,
    canopy: t.canopy, frontRoad: t.frontRoad, service: t.service, lanes: p.lanes, access: p.access,
    buffer: intersection(p.streetZone, green), parking, green,
    carAccess: [sf.a[0] + sf.u[0] * p.sAccess, sf.a[1] + sf.u[1] * p.sAccess],
    truckGates: truckGates(p, t.service),
    pylon: [sf.a[0] + sf.u[0] * (p.sAccess + 9) + sf.n[0] * (p.prog.streetBuffer / 2), sf.a[1] + sf.u[1] * (p.sAccess + 9) + sf.n[1] * (p.prog.streetBuffer / 2)],
    streetFrame: sf,
  };
}

/**
 * The longest row that still works. A bisection on the trim estimates the
 * car park from its area, at the rate a quick packing of the untrimmed one
 * gave; then quick packings (two row offsets, a subset of the full eight, so
 * never more spaces than the full one) step the trim down until the spaces
 * and the green really fit, and up while they still do. Only the options
 * that will be shown get the full packing.
 */
interface Solved { p: Prepared; k: number; t: Trial; rows: Rows; limitedBy: string[] }
function solve(p: Prepared, rules: Rules, plotArea: number): Solved {
  const lim = limits(rules, plotArea);
  const full = trial(p, rules, 1);
  const quick = packRegion(p, full.region, { quick: true });
  const regionArea = area(full.region);
  const perStall = quick.stalls.length ? regionArea / quick.stalls.length : 30;
  const aislePerStall = quick.stalls.length ? area(quick.aisles) / quick.stalls.length : 0;
  const padStalls = p.pads.reduce((s, x) => s + x.stalls.length, 0);
  const tree = p.prog.treeEvery > 0 ? p.prog.treeEvery / (p.prog.treeEvery + 1) : 1;
  const hard = (t: Trial): string[] => {
    const out: string[] = [];
    if (t.projection > lim.maxProjection + 1e-6) out.push("POT");
    if (t.gross + (rules.existing?.gross ?? 0) > lim.maxGross + 1e-6) out.push("CUT");
    if (p.prog.maxGla > 0 && t.gla > p.prog.maxGla) out.push("suprafața închiriabilă cerută");
    return out;
  };
  const estimate = (t: Trial): string[] => {
    const out: string[] = [];
    const packed = area(t.region) / perStall;
    let cap = packed * tree + padStalls;
    if (rules.minGreen !== undefined) {
      const short = (rules.minGreen / 100) * plotArea - greenArea(p, t, packed * tree, packed * aislePerStall);
      if (short > 0) { cap -= short / STALL_M2; if (cap < t.required) out.push("spațiul verde"); }
    }
    if (cap < t.required && !out.length) out.push("parcarea");
    return [...out, ...hard(t)];
  };
  // the same test with a real (quick) packing: spaces after tree islands and planting for the green share
  const real = (t: Trial): string[] => {
    const pk = packRegion(p, t.region, { quick: true, rows: quick.rows });
    const short = rules.minGreen !== undefined ? (rules.minGreen / 100) * plotArea - greenArea(p, t, pk.stalls.length, area(pk.aisles)) : 0;
    const fin = finishParking(pk, [], p.prog.treeEvery, t.required, padStalls, short);
    const out: string[] = [];
    const green = short - (fin.islands.length) * STALL_M2;
    if (green > 1e-6) out.push("spațiul verde");
    else if (fin.stalls.length + padStalls < t.required) out.push("parcarea");
    return [...out, ...hard(t)];
  };
  let k = 1;
  let t = full;
  let limitedBy = real(full);
  if (limitedBy.length) {
    let lo = 0, hi = 1;
    for (let i = 0; i < 9; i++) { const mid = (lo + hi) / 2; if (estimate(trial(p, rules, mid)).length) hi = mid; else lo = mid; }
    k = lo;
    t = trial(p, rules, k);
    let why = real(t);
    for (let guard = 0; guard < 25 && k > 0 && why.length; guard++) { limitedBy = why; k = Math.max(0, k - 0.02); t = trial(p, rules, k); why = real(t); }
    for (let guard = 0; guard < 10 && !why.length && k < 1; guard++) {
      const up = Math.min(1, k + 0.02), tu = trial(p, rules, up), wu = real(tu);
      if (wu.length) { limitedBy = wu; break; }
      k = up; t = tu;
    }
  } else limitedBy = ["terenul: retrageri și curtea de livrare"];
  return { p, k, t, rows: quick.rows, limitedBy };
}

export function generateRetail(site: Site, rules: Rules, prog: RetailProgramme, limit = 6): RetailResult {
  // the envelope at the boxes' own cornice: height-dependent setbacks read it
  const env = envelope(site, rules, { ...DEFAULT_PROGRAMME, groundStoreyHeight: prog.height, storeyHeight: prog.height });
  if (!env.storeys.length) {
    return { envelope: env, options: [], message: env.limitedBy === "empty" ? "Retragerile nu lasă loc de clădire." : `Înălțimea maximă (${fmt(rules.maxHeight, 1)} m) e sub cornișa halelor (${fmt(prog.height, 1)} m).` };
  }
  const b = readPlot(env, site);
  const kinds = typologies(b);
  if (!kinds.length) return { envelope: env, options: [], message: "Nu găsesc o latură de spate de cel puțin 20 m de care să se sprijine rândul de unități." };
  const solved: Solved[] = [];
  for (const typology of kinds) for (const depth of prog.depths) {
    const p = prepare(b, prog, typology, depth);
    if (p) solved.push(solve(p, rules, env.plotArea));
  }
  // rank on the quick figures, keep one of each distinct building, and draw only those in full
  const glaOf = (x: Solved) => x.t.gla;
  const seen = new Set<string>();
  const options = solved.sort((x, y) => glaOf(y) - glaOf(x)).filter((x) => {
    const key = `${Math.round(x.t.gla / 10)}|${Math.round(x.t.required)}`;
    return seen.has(key) ? false : (seen.add(key), true);
  }).slice(0, limit).map((x) => {
    // The full packing can still disagree with the quick one: its row
    // pattern makes more aisle, or more tree islands. Step back until it fits.
    let { k, t } = x;
    let s = scheme(x.p, rules, k, t, x.rows);
    const fails = (sc: Scheme, tr: Trial) => capacityOf(sc) < tr.required || (rules.minGreen !== undefined && (area(sc.green) / env.plotArea) * 100 < rules.minGreen - 1e-6);
    for (let guard = 0; guard < 12 && k > 0 && fails(s, t); guard++) {
      k = Math.max(0, k - 0.02);
      t = trial(x.p, rules, k);
      s = scheme(x.p, rules, k, t, x.rows);
    }
    return s.units.length ? finish(env, site, rules, prog, x.p, s, t, x.limitedBy) : null;
  }).filter((g): g is RetailGenerated => !!g).sort((x, y) => y.gla - x.gla || x.id.localeCompare(y.id));
  return {
    envelope: env, options: options.slice(0, limit),
    message: options.length ? undefined : "Nicio variantă: curtea de livrare și adâncimea cerută nu încap în anvelopă.",
  };
}

function finish(env: Envelope, site: Site, rules: Rules, prog: RetailProgramme, p: Prepared, s: Scheme, t: Trial, limitedBy: string[]): RetailGenerated {
  const padGla = s.pads.reduce((x, pd) => x + pd.gla, 0);
  const gla = s.units.reduce((x, u) => x + u.gla, 0) + padGla;
  const gross = area(s.building) + s.pads.reduce((x, pd) => x + pd.area, 0);
  const ind = indicators(rules, env.plotArea, [union(s.building, ...s.pads.map((x) => x.building))], [s.canopy], gross);
  const onPads = s.pads.reduce((x, pd) => x + pd.stalls.length, 0);
  const capacity = s.parking.stalls.length + onPads;
  const byLaw = requiredByLaw(rules, gross, gla);
  const target = Math.ceil((prog.parkingTarget * gla) / 100);
  const walk = [...s.parking.walk].sort((x, y) => x - y);
  const stallArea = area(union(s.parking.aisles, ...s.parking.stalls, ...s.pads.flatMap((x) => [x.aisles, ...x.stalls])));
  const roads = area(union(s.access, s.frontRoad, ...s.pads.map((x) => x.lane)));
  const service = area(union(s.service, s.lanes));
  const built = area(union(s.building, ...s.pads.map((x) => x.building)));
  const balance: RetailBalance = {
    plot: env.plotArea, built, canopy: area(s.canopy), roads, parking: stallArea, service, green: area(s.green),
  };
  const parking: RetailParking = {
    required: Math.max(byLaw, target), byLaw, target, fromRules: rules.parkingPerGla !== undefined, capacity, onPads,
    accessible: s.parking.accessible.length, ev: s.parking.ev.length, islands: s.parking.islands.length, converted: s.parking.converted,
    walkMax: walk.length ? walk[walk.length - 1] : 0, walkMedian: walk.length ? walk[Math.floor(walk.length / 2)] : 0,
    perSpace: capacity ? (stallArea + roads) / capacity : 0,
  };
  const kinds: (RetailKind | "pad")[] = ["anchor", "medium", "small"];
  const byKind = [
    ...kinds.map((k) => { const us = s.units.filter((u) => u.kind === k); return { kind: k, count: us.length, gla: us.reduce((x, u) => x + u.gla, 0) }; }),
    { kind: "pad" as const, count: s.pads.length, gla: padGla },
  ].filter((x) => x.count);
  const name = { line: "linie", "L-left": "L-st", "L-right": "L-dr", U: "U" }[s.typology];
  // what was built, which trimming may have made simpler than what was tried: an L whose side wing went is a row
  const sides = s.spines.map((sp, i) => (sp.role !== "rear" && s.wings[i].length ? sp.role : null)).filter(Boolean);
  const shape: RetailTypology = sides.length === 2 ? "U" : sides.length === 1 ? (sides[0] === "left" ? "L-left" : "L-right") : "line";
  const g: RetailGenerated = {
    id: `${name}-${s.depth}`, typology: shape, scheme: s, footprintArea: built, grossArea: gross, gla, height: prog.height,
    indicators: ind, parking, balance, byKind, binding: limitedBy, checks: [],
  };
  g.checks = retailCompliance(env, site, rules, prog, g);
  return g;
}

// ---------- compliance ----------

export function retailCompliance(env: Envelope, site: Site, rules: Rules, prog: RetailProgramme, g: RetailGenerated): Check[] {
  const local = (k: keyof NonNullable<Rules["sources"]>) => rules.sources?.[k] ?? "CU / PUZ";
  const lim = limits(rules, env.plotArea);
  const catuc = (rules.regime ?? "catuc") === "catuc";
  const der = DEROGATION[rules.derogation ?? "none"];
  const s = g.scheme, ind = g.indicators, pk = g.parking;
  const withDerogation = (base: string, v: number, dp: number, unit = "") =>
    lim.factor ? `≤ ${fmt(v, dp)}${unit} (${base}${unit} + ${Math.round(lim.factor * 100)}%)` : `≤ ${fmt(v, dp)}${unit}`;
  const checks: Check[] = [
    {
      rule: catuc ? "POT (proiecție, cu copertina)" : "POT (amprentă la sol)", value: `${fmt(ind.pot, 1)}%`, limit: withDerogation(fmt(rules.pot, 1), lim.potLimit, 1, "%"),
      verdict: ind.pot <= lim.potLimit + 1e-6 ? "pass" : "fail", source: `${local("pot")}; ${src(POT_SOURCE)}`, verified: POT_SOURCE.verified,
      note: catuc ? `Copertina de ${fmt(prog.canopyDepth, 1)} m din fața magazinelor (${fmt(ind.balconies)} m²) e un element construit permanent în afara amprentei: intră în proiecție. Parcarea la sol și aleile nu intră (lectura noastră a anexei). ${POT_SOURCE.note}` : "Plan aprobat înainte de 25.08.2026: amprenta la sol.",
    },
    { rule: "CUT", value: fmt(ind.cut), limit: withDerogation(fmt(rules.cut), lim.cutLimit, 2), verdict: ind.cut <= lim.cutLimit + 1e-6 ? "pass" : "fail", source: `${local("cut")}; ${src(CUT_SOURCE)}`, verified: CUT_SOURCE.verified, note: "Un singur nivel: CUT e amprenta clădirilor (fără copertină) peste teren." },
    { rule: "Înălțime la cornișă", value: `${fmt(g.height, 1)} m`, limit: `≤ ${fmt(rules.maxHeight, 1)} m`, verdict: g.height <= rules.maxHeight + 1e-9 ? "pass" : "fail", source: local("maxHeight"), verified: true },
  ];
  if (rules.cut > 4) checks.push({ rule: "CUT peste 4", value: fmt(rules.cut), limit: "numai prin PUG", verdict: (rules.indicatorsFrom ?? "PUZ") === "PUG" ? "pass" : "fail", source: src(CUT_ABOVE_4_SOURCE), verified: true });
  if (rules.existing && (rules.existing.footprint > 0 || rules.existing.gross > 0)) checks.push({ rule: "Construcții existente păstrate", value: `${fmt(rules.existing.footprint)} m² Ac · ${fmt(rules.existing.gross)} m² Adc`, limit: "se adună la indicatori", verdict: "info", source: src(EXISTING_SOURCE), verified: true });
  if (lim.factor) checks.push({ rule: "Derogare simulată", value: der.label, limit: "o singură dată", verdict: "info", source: src(der.source), verified: true, note: der.condition });
  checks.push({ rule: "Retrageri", value: `${env.storeys[0].setbacks.map((x) => fmt(x, 1)).join(" / ")} m`, limit: "din PUZ, minim Codul civil", verdict: "pass", source: `${local("setbacks")}; ${src(MIN_SETBACK_SOURCE)}`, verified: MIN_SETBACK_SOURCE.verified, note: "Respectat prin construcție: halele și pad-urile stau în anvelopă. Curtea de livrare și parcarea pot sta în retrageri." });

  const greenPct = (g.balance.green / g.balance.plot) * 100;
  checks.push({
    rule: "Spații verzi", value: `${fmt(greenPct, 1)}%${pk.converted ? ` (${pk.converted} locuri date spațiului verde)` : ""}`,
    limit: rules.minGreen !== undefined ? `≥ ${fmt(rules.minGreen, 0)}%` : "nespecificat în CU / PUZ",
    verdict: rules.minGreen === undefined ? "info" : greenPct >= rules.minGreen - 1e-6 ? "pass" : "fail", source: local("green"), verified: rules.minGreen !== undefined,
    note: `Fâșia plantată de ${fmt(prog.streetBuffer, 1)} m la stradă, ${pk.islands - pk.converted} insule cu arbori în parcare${prog.treeEvery ? ` (una la ${prog.treeEvery} locuri)` : ""} și ce rămâne liber. Totul pe teren natural: nu e subsol.`,
  });
  checks.push({
    rule: "Locuri de parcare, după normă", value: `${pk.capacity}${pk.onPads ? ` (din care ${pk.onPads} pe loturile pad-urilor)` : ""}`,
    limit: `≥ ${pk.byLaw} (${pk.fromRules ? `${fmt(rules.parkingPerGla!, 1)} / 100 m² GLA` : "trepte RGU pe Adc"})`,
    verdict: pk.capacity >= pk.byLaw ? "pass" : "fail", source: pk.fromRules ? local("parking") : src(RETAIL_PARKING_SOURCE), verified: pk.fromRules, note: pk.fromRules ? undefined : RETAIL_PARKING_SOURCE.note,
  });
  checks.push({
    rule: "Locuri de parcare, ținta chiriașilor", value: `${pk.capacity} (${fmt((pk.capacity / Math.max(1, g.gla)) * 100, 1)} / 100 m² GLA)`,
    limit: `≥ ${pk.target} (${fmt(prog.parkingTarget, 1)} / 100 m² GLA)`, verdict: pk.capacity >= pk.target ? "pass" : "fail", source: "Programul dezvoltatorului", verified: true,
    note: "Ce cer de obicei ancorele și rețelele: nu e lege, dar un retail park sub ținta asta se închiriază greu.",
  });
  checks.push({ rule: "Locuri pentru persoane cu dizabilități", value: `${pk.accessible}`, limit: `≥ ${Math.round(ACCESSIBLE_SHARE * 100)}%, minim 1`, verdict: "info", source: src(ACCESSIBLE_SOURCE), verified: false, note: "Desenate ca locurile cele mai apropiate de intrări." });
  checks.push({ rule: "Locuri cu încărcare electrică", value: `${pk.ev}`, limit: `${Math.round(EV_SHARE_NONRES * 100)}%`, verdict: "info", source: src(EV_NONRES_SOURCE), verified: false, note: EV_NONRES_SOURCE.note });
  checks.push({
    rule: "Distanța de mers loc–intrare", value: `max ${fmt(pk.walkMax, 0)} m · median ${fmt(pk.walkMedian, 0)} m`, limit: `≤ ${MAX_WALK_TO_ENTRANCE} m`,
    verdict: pk.walkMax <= MAX_WALK_TO_ENTRANCE ? "pass" : "fail", source: src(WALK_SOURCE), verified: false, note: WALK_SOURCE.note,
  });

  // lorries: every dock needs the apron its lorry reverses across
  const docks = s.units.flatMap((u) => u.service.filter((o) => o.kind === "dock").map((o) => ({ u, o })));
  const shortDocks = docks.filter(({ o }) => o.apron < DOCK_APRON[o.vehicle] - 0.1);
  const minApron = docks.length ? Math.min(...docks.map(({ o }) => o.apron)) : 0;
  checks.push({
    rule: "Curte de livrare în fața cheiurilor", value: docks.length ? (shortDocks.length ? `${shortDocks.length} din ${docks.length} cheiuri prea scurte (min ${fmt(minApron, 1)} m)` : `${docks.length} cheiuri, min ${fmt(minApron, 1)} m`) : "fără cheiuri",
    limit: `TIR ≥ ${DOCK_APRON.articulated} m · camion ≥ ${DOCK_APRON.rigid} m`, verdict: shortDocks.length ? "fail" : "pass", source: src(DOCK_APRON_SOURCE), verified: false, note: DOCK_APRON_SOURCE.note,
  });
  checks.push({
    rule: "Acces camioane din stradă", value: s.truckGates.length ? `${s.truckGates.length} ${s.truckGates.length === 1 ? "poartă" : "porți"} de serviciu` : "lipsă",
    limit: "separat de accesul clienților", verdict: s.truckGates.length ? "pass" : "fail", source: "CU / PUZ, aviz drumuri", verified: false,
    note: s.truckGates.length ? `Cea mai apropiată poartă de serviciu e la ${fmt(Math.min(...s.truckGates.map((q) => Math.hypot(q[0] - s.carAccess[0], q[1] - s.carAccess[1]))), 0)} m de intrarea auto. Avizul de drumuri decide pozițiile și benzile de decelerare.` : "Nicio latură nu duce camioanele din stradă în curtea de livrare.",
  });
  // fire engines: each unit has a road along its shopfront or its yard
  const roads = union(s.frontRoad, s.access, s.service, s.lanes, s.parking.aisles);
  const unreachable = s.units.filter((u) => distanceTo(u.entrance, roads) > 12 && u.service.every((o) => distanceTo([(o.a[0] + o.b[0]) / 2 + o.normal[0], (o.a[1] + o.b[1]) / 2 + o.normal[1]], roads) > 3));
  checks.push({ rule: "Acces pompieri la fațade", value: unreachable.length ? `${unreachable.length} unități fără drum la fațadă` : "toate unitățile", limit: `drum ≥ ${fmt(FIRE_ROAD_WIDTH, 1)} m în față sau în spate`, verdict: unreachable.length ? "fail" : "pass", source: src(FIRE_ACCESS_SOURCE), verified: false, note: FIRE_ACCESS_SOURCE.note });
  // escape: the farthest point of a unit from its front entrance or a back door
  let farthest = 0;
  for (const u of s.units) {
    const exits = [u.entrance, ...u.service.map((o) => [(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2] as Pt)];
    for (const q of u.polygon[0][0]) farthest = Math.max(farthest, Math.min(...exits.map((e) => Math.hypot(e[0] - q[0], e[1] - q[1]))));
  }
  checks.push({ rule: "Distanța de evacuare în unitate", value: `max ${fmt(farthest, 1)} m`, limit: `≤ ${MAX_EXIT_DISTANCE_RETAIL} m`, verdict: farthest <= MAX_EXIT_DISTANCE_RETAIL ? "pass" : "fail", source: src(EXIT_RETAIL_SOURCE), verified: false, note: EXIT_RETAIL_SOURCE.note });

  if (site.context?.length) {
    const masses = [{ footprint: union(s.building, ...s.pads.map((x) => x.building)), base: 0, top: g.height }];
    const o3 = overshadowing(site.context.map((m) => ({ ...m, heightFrom: m.heightFrom ?? "assumed" })), masses, env.plot, site.latitude ?? DEFAULT_LATITUDE);
    checks.push({ rule: "Umbrirea clădirilor vecine", value: o3.lost ? `${o3.lost} din ${o3.points} puncte sub 1½ h` : `niciun punct din ${o3.points} sub 1½ h din cauza noastră`, limit: "vecinii păstrează 1½ h la solstițiul de iarnă", verdict: o3.lost ? "fail" : "pass", source: src(SUNLIGHT_SOURCE), verified: SUNLIGHT_SOURCE.verified });
  }
  return checks;
}

